import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useCart } from "../context/AppContext";
import { CheckCircle2, XCircle, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";

function ReferenceCard({ sessionId, amount }) {
  const copy = () => { navigator.clipboard.writeText(sessionId); toast.success("Reference copied"); };
  return (
    <div className="mt-8 border border-[color:var(--bl-gold)]/40 bg-[color:var(--bl-gold)]/5 p-6 text-left" data-testid="order-reference-card">
      <div className="bl-eyebrow text-[color:var(--bl-gold)]">Payment reference</div>
      <button onClick={copy} data-testid="order-reference-copy" className="mt-2 inline-flex items-center gap-3 font-mono text-white text-2xl tracking-[0.2em] hover:text-[color:var(--bl-gold)] transition-colors">
        {sessionId} <Copy size={16} className="opacity-50" />
      </button>
      <div className="font-mono text-xs text-white/60 mt-2" data-testid="order-amount-due">Amount due A${(amount || 0).toFixed(2)}</div>
    </div>
  );
}

function WalletRow({ label, value }) {
  const copy = () => { navigator.clipboard.writeText(value); toast.success(`${label} address copied`); };
  return (
    <button onClick={copy} className="w-full text-left border border-[color:var(--bl-gold)]/30 bg-[color:var(--bl-gold)]/5 px-3 py-2.5 hover:bg-[color:var(--bl-gold)]/10 transition-colors">
      <div className="bl-eyebrow text-[color:var(--bl-gold)]">{label}</div>
      <div className="font-mono text-xs text-white break-all mt-1 flex items-center gap-2">{value} <Copy size={12} className="opacity-50 shrink-0" /></div>
    </button>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const [status, setStatus] = useState(null);
  const [instructions, setInstructions] = useState("");
  const [wallets, setWallets] = useState({});
  const [error, setError] = useState(null);
  const { clear } = useCart();

  useEffect(() => {
    if (!session_id) return;
    clear();
    Promise.all([
      axios.get(`${API}/payments/status/${session_id}`),
      axios.get(`${API}/payments/instructions`),
    ]).then(([s, i]) => { setStatus(s.data); setInstructions(i.data.instructions); setWallets(i.data); })
      .catch(err => setError(err?.response?.data?.detail || "We couldn't find that order. Check the reference in your email."));
  }, [session_id, clear]);

  const paid = status?.payment_status === "paid";
  return (
    <div className="min-h-screen bg-black pt-32 pb-20 px-6">
      <div className="max-w-lg mx-auto text-center">
        {status || error ? <CheckCircle2 size={56} className="mx-auto text-[color:var(--bl-blue)]" /> :
                           <Loader2 size={56} className="mx-auto text-white/60 animate-spin" />}
        <div className="bl-eyebrow mt-8">{paid ? "Confirmed" : "Order received"}</div>
        <h1 className="bl-heading text-white text-4xl mt-4" data-testid="order-success-heading">{paid ? "Payment Confirmed" : "Almost There"}</h1>
        <p className="text-white/60 mt-4">
          {paid
            ? `Thank you. Your payment of A$${(status.amount || 0).toFixed(2)} has been received.`
            : "Your order is reserved. Complete payment using the details below — we've also emailed them to you."}
        </p>
        {status && session_id && <ReferenceCard sessionId={session_id} amount={status.amount} />}
        {!paid && instructions && (
          <div className="mt-6 border border-white/10 bg-white/[0.02] p-6 text-left" data-testid="payment-instructions">
            <div className="bl-eyebrow text-white/40 mb-3">How to pay</div>
            <div className="text-white/80 text-sm leading-relaxed whitespace-pre-line">{instructions}</div>
            {(wallets.wallet_btc || wallets.wallet_usdt) && (
              <div className="mt-4 space-y-2" data-testid="payment-wallets">
                {wallets.wallet_btc && <WalletRow label="Bitcoin (BTC)" value={wallets.wallet_btc} />}
                {wallets.wallet_usdt && <WalletRow label={`USDT${wallets.wallet_usdt_network ? ` · ${wallets.wallet_usdt_network}` : ""}`} value={wallets.wallet_usdt} />}
              </div>
            )}
          </div>
        )}
        {error && (
          <div data-testid="payment-error" className="mt-6 border border-red-500/40 bg-red-500/5 text-red-200 text-sm px-4 py-3">{error}</div>
        )}
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/shop" className="bl-btn">Continue Shopping</Link>
          {session_id && (
            <Link to={`/track/${session_id}`} className="bl-btn gold" data-testid="success-track-order">Track Order</Link>
          )}
          <Link to="/account" className="bl-btn primary" data-testid="success-view-account">View Account</Link>
        </div>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-black pt-32 pb-20 px-6">
      <div className="max-w-lg mx-auto text-center">
        <XCircle size={56} className="mx-auto text-white/60" />
        <div className="bl-eyebrow mt-8">Cancelled</div>
        <h1 className="bl-heading text-white text-4xl mt-4">Checkout Cancelled</h1>
        <p className="text-white/60 mt-4">Your cart is intact. Ready when you are.</p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/shop" className="bl-btn">Back to Shop</Link>
        </div>
      </div>
    </div>
  );
}
